import { useCallback } from 'react'
import EvaluationService from '../../../services/evaluation_service'
import { evaluationDefaults } from '../../../models/evaluation'

import { useTenant } from '../../../hooks/useTenant'
import { toast } from 'react-toastify'
import { toISODate } from '../../../utils/date'
import { doc, updateDoc, arrayUnion } from 'firebase/firestore'
import { db } from '../../../services/firebase'
import XpService, { XP_ACTION_TYPES } from '../../../services/xp_service'
import GamificationService from '../../../services/gamification_service'
import GamificationEngineService from '../../../services/gamification_engine'
import { calculateMemberEvaluation } from './utils/evaluationCalculator'

export const useSaveEvaluations = ({
    idActivity,
    classId,
    members,
    excludedIds,
    draftLevelsByTopicId,
    allTopicIds,
    topicMetaById,
    defaultLevelId,
    levels,
    withLoading,
    activeEventId,
    classTeacherId,
    classEmployeeName,
    activityName,
    eventName,
}) => {
    const { idTenant, user } = useTenant()

    const saveAll = useCallback(async () => {
        if (!withLoading || !idActivity || !activeEventId) return

        const excludedSet = new Set(excludedIds || [])
        const evaluationMembers = members.filter(c => !excludedSet.has(String(c.id)))

        if (evaluationMembers.length === 0) {
            toast.warning("Nenhum aluno selecionado para avaliação")
            return
        }

        if (!user || !user.uid) {
            toast.error("Usuário não identificado. Tente recarregar a página.")
            return
        }

        await withLoading('saveAll', async () => {
            try {
                // ─── Buscar configurações de gamificação ───
                let gamificationSettings = null
                try {
                    gamificationSettings = await GamificationService.getSettings(idTenant)
                } catch (err) {
                    console.warn('[Evaluation] Não foi possível carregar config de gamificação:', err)
                }

                // Encontrar o nível base (menor ordem - Nível 0) para preenchimento automático
                const baseLevel = levels && levels.length > 0
                    ? levels.reduce((prev, curr) => ((curr.order || 0) < (prev.order || 0) ? curr : prev), levels[0])
                    : null

                // Determinar o nível máximo para cálculos de XP
                const maxLevel = levels && levels.length > 0
                    ? levels.reduce((prev, curr) => ((Number(curr.order || 0)) > (Number(prev.order || 0)) ? curr : prev), levels[0])
                    : null
                const maxLevelId = String(maxLevel?.id || '')
                const maxLevelValue = Number(maxLevel?.order || maxLevel?.value || 0)

                // Lista de membros que ganharam XP (para GamificationEngine no final)
                const membersWithXp = []

                // ─── ANTI-DUPLICIDADE E EDIÇÃO ───
                const memberIdsStr = evaluationMembers.map(c => String(c.id))
                
                // 1. Buscar a avaliação do CICLO ATUAL (Evento), para atualizarmos se já existir
                let currentCycleEvals = []
                if (activeEventId) {
                    currentCycleEvals = await EvaluationService.getByActivityAndEvent(idTenant, idActivity, activeEventId)
                }

                // 2. Buscar a ÚLTIMA avaliação de cada aluno (do ciclo atual ou histórico passado)
                const latestEvalsMap = await EvaluationService.getLatestByMemberAndActivity(idTenant, idActivity, memberIdsStr)

                // Preparar dados e Promises de salvamento
                const savePromises = evaluationMembers.map(async (member) => {
                    const idMember = String(member.id)
                    
                    const docToEdit = currentCycleEvals.find(e => String(e.idMember || e.idMember) === idMember)
                    const previousEval = latestEvalsMap[idMember] || null

                    // ─── Motor Único de Cálculo PURO ───
                    const calcResult = calculateMemberEvaluation({
                        memberId: idMember,
                        previousEval,
                        allTopicIds,
                        topicMetaById,
                        draftLevelsByTopicId,
                        levels,
                        baseLevel,
                        maxLevelId,
                        maxLevelValue,
                        gamificationSettings
                    });

                    if (calcResult.isEmpty) return null;

                    const {
                        objectivesToSave,
                        topicXpTotal,
                        topicXpDetails,
                        memberPassed,
                        progressPercent,
                        givePassReward
                    } = calcResult;

                    // Registramos a avaliação "mestre" para o aluno neste ciclo
                    const evaluationId = await EvaluationService.registerEvaluation(idTenant, user, {
                        ...evaluationDefaults,
                        id: docToEdit?.id, // UPDATE se já tiver no ciclo atual
                        idMember: idMember,
                        memberName: member.name,
                        memberPhone: member.phone || null,
                        memberEmail: member.email || null,
                        memberPhotoUrl: member.photo || null,
                        idActivity: idActivity,
                        activityName: activityName || "",
                        idEvent: activeEventId,
                        eventName: eventName || "",
                        idClass: classId || null,
                        idEmployee: classTeacherId || user.uid,
                        employeeName: classTeacherId ? classEmployeeName : (user.displayName || user.fullName || user.email || "Sistema"),
                        teacherName: classEmployeeName || "",
                        idLevel: defaultLevelId,
                        date: toISODate(new Date()),
                        objectives: objectivesToSave,
                        status: memberPassed ? 'PASSED' : 'FAILED',
                        progressPercent: progressPercent,
                        xpTopicAwarded: topicXpTotal > 0,
                        xpPassAwarded: memberPassed,
                        xpTopicTotal: topicXpTotal
                    })

                    // ─── Entrega de XP (pós-save) ───
                    try {
                        // Lógica 1: XP por tópico
                        if (topicXpTotal > 0) {
                            await XpService.addXp(idTenant, {
                                idMember: idMember,
                                xpAmount: topicXpTotal,
                                actionType: XP_ACTION_TYPES.EVALUATION_PASS,
                                sourceId: evaluationId,
                                sourceName: `Tópicos com nota máxima`,
                                description: topicXpDetails.map(t => `${t.name}: +${t.xp} XP`).join(', '),
                                user
                            })
                            if (!membersWithXp.includes(idMember)) membersWithXp.push(idMember)
                        }

                        // Lógica 2: XP por passagem de nível
                        if (givePassReward) {
                            // Registra o ID da atividade no array `passedActivityIds` do Aluno (Custo de Leitura 0 futuramente para renderizar insignías!)
                            try {
                                const memberRef = doc(db, 'tenants', idTenant, 'members', idMember);
                                await updateDoc(memberRef, {
                                    passedActivityIds: arrayUnion(idActivity)
                                });
                            } catch (err) {
                                console.error("Erro ao registrar a insignía no membro:", err);
                            }

                            // XP pro aluno
                            if (gamificationSettings?.xpRewardMemberPass > 0) {
                                await XpService.addXp(idTenant, {
                                    idMember: idMember,
                                    xpAmount: gamificationSettings.xpRewardMemberPass,
                                    actionType: XP_ACTION_TYPES.EVALUATION_PASS,
                                    sourceId: evaluationId,
                                    sourceName: `Passagem de Nível`,
                                    description: `Aprovado com ${progressPercent}% (mínimo: ${gamificationSettings.minScoreToPassPercent}%)`,
                                    user
                                })
                                if (!membersWithXp.includes(idMember)) membersWithXp.push(idMember)
                            }

                            // XP pro professor
                            if (gamificationSettings?.xpRewardTeacherPass > 0 && user?.uid) {
                                await XpService.addXp(idTenant, {
                                    idMember: user.uid,
                                    isEmployee: true,
                                    xpAmount: gamificationSettings.xpRewardTeacherPass,
                                    actionType: XP_ACTION_TYPES.EVALUATION_PASS,
                                    sourceId: evaluationId,
                                    sourceName: `Aluno aprovado: ${member.name}`,
                                    description: `Professor recompensado pela aprovação do aluno`,
                                    user
                                })
                            }
                        }
                    } catch (xpErr) {
                        console.error('[Evaluation XP] Erro ao entregar XP:', xpErr)
                        // Não bloqueia o salvamento da avaliação
                    }

                    return { member, objectives: objectivesToSave, memberPassed, topicXpTotal, progressPercent }
                })

                const results = await Promise.all(savePromises)

                // ─── Disparo do Motor de Gamificação (Background) ───
                // Segue o MESMO padrão do AttendanceService
                if (membersWithXp.length > 0 && gamificationSettings) {
                    Promise.allSettled(
                        membersWithXp.map(idMember =>
                            GamificationEngineService.evaluateMemberGamification(
                                idTenant, idMember, gamificationSettings, user
                            )
                        )
                    ).catch(err => console.error('[Evaluation] Erro no GamificationEngine:', err))
                }

                // Resumo
                const passedCount = results.filter(r => r?.memberPassed).length
                const xpCount = results.filter(r => r?.topicXpTotal > 0).length
                let msg = `Avaliações salvas com sucesso!`
                if (passedCount > 0) msg += ` ${passedCount} aluno(s) aprovado(s).`
                if (xpCount > 0) msg += ` XP entregue para ${xpCount} aluno(s).`
                toast.success(msg)

            } catch (error) {
                console.error("Erro ao salvar avaliações:", error)
                toast.error(error.message || "Erro ao salvar avaliações")
            }
        })
    }, [
        idTenant, idActivity, classId, members, excludedIds,
        draftLevelsByTopicId, allTopicIds, topicMetaById, defaultLevelId,
        levels, withLoading, activeEventId, classTeacherId, classEmployeeName, activityName, eventName,
        user
    ])

    return { saveAll }
}
